import { useState, type FormEvent, type ReactNode } from 'react';
import { Button } from './Button';
import { Field } from './Field';
import { Input } from './Input';

export interface LoginFormProps {
  /** Resolve on success; throw an Error whose message is shown to the person. */
  onSubmit: (username: string, password: string) => Promise<void>;
  title?: string;
  subtitle?: ReactNode;
  size?: 'md' | 'lg' | 'touch';
  footer?: ReactNode;
}

export function LoginForm({
  onSubmit,
  title = 'Sign in',
  subtitle,
  size = 'lg',
  footer,
}: LoginFormProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await onSubmit(username, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => void submit(e)}
      noValidate
      style={{ display: 'grid', gap: 'var(--space-4)', width: '100%' }}
    >
      <div>
        <h1 style={{ margin: 0, fontSize: 'var(--fs-xl)' }}>{title}</h1>
        {subtitle ? (
          <p style={{ margin: '4px 0 0', color: 'var(--fg-muted)' }}>{subtitle}</p>
        ) : null}
      </div>
      {error ? (
        <div
          role="alert"
          style={{
            padding: 'var(--space-3)',
            borderRadius: 'var(--r-md)',
            background: 'var(--bad-bg)',
            color: 'var(--bad)',
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      ) : null}
      <Field label="Username" required>
        {(p) => (
          <Input
            {...p}
            size={size}
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        )}
      </Field>
      <Field label="Password" required>
        {(p) => (
          <Input
            {...p}
            size={size}
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
      </Field>
      <Button
        type="submit"
        variant="cta"
        size={size === 'touch' ? 'touch' : 'lg'}
        block
        loading={busy}
      >
        Sign in
      </Button>
      {footer}
    </form>
  );
}
